import { CHART_MAX_SERIES } from '../constants/charts';
import formatDatasourceCell from './formatDatasourceCell';
import readDatasourceNumber from './readDatasourceNumber';

import type { ILineChartData, ILineChartSeries } from '../interfaces/IAiChatChartData';
import type { IAiChatDatasourceColumn, ILineChartShown } from '../interfaces/IAiChatTools';

const indexOf = (columns: IAiChatDatasourceColumn[], name: string): number => (
  columns.findIndex((column) => column.name === name)
);

/** Wide shape: every row is one point, and every y column is one line, named as the tool resolved it. */
const buildWide = (
  columns: IAiChatDatasourceColumn[],
  rows: unknown[][],
  shown: ILineChartShown,
): ILineChartData => {
  const xIndex = indexOf(columns, shown.x.column.name);
  const labels = rows.map((row) => formatDatasourceCell(row[xIndex], shown.x.column.pgType));

  const series = shown.y.columns.map((column, position): ILineChartSeries => {
    const valueIndex = indexOf(columns, column.name);

    return {
      name: shown.y.labels?.[position] || column.name,
      values: rows.map((row) => readDatasourceNumber(row[valueIndex])),
    };
  });

  return {
    labels,
    series: series.slice(0, CHART_MAX_SERIES),
    droppedSeries: Math.max(0, series.length - CHART_MAX_SERIES),
  };
};

/** Long shape: rows are split into lines by the series column, and aligned on the x labels. */
const buildLong = (
  columns: IAiChatDatasourceColumn[],
  rows: unknown[][],
  shown: ILineChartShown,
  seriesColumn: IAiChatDatasourceColumn,
): ILineChartData => {
  const xIndex = indexOf(columns, shown.x.column.name);
  const seriesIndex = indexOf(columns, seriesColumn.name);
  const valueIndex = indexOf(columns, shown.y.columns[0]?.name ?? '');

  // Labels and series both in order of first appearance: the statement chose the order.
  const labels: string[] = [];
  const labelPositions = new Map<string, number>();
  const valuesBySeries = new Map<string, Map<number, number | null>>();

  rows.forEach((row) => {
    const label = formatDatasourceCell(row[xIndex], shown.x.column.pgType);
    const name = formatDatasourceCell(row[seriesIndex], seriesColumn.pgType);

    if (!labelPositions.has(label)) {
      labelPositions.set(label, labels.length);
      labels.push(label);
    }

    if (!valuesBySeries.has(name)) {
      valuesBySeries.set(name, new Map());
    }

    valuesBySeries.get(name)?.set(labelPositions.get(label) ?? 0, readDatasourceNumber(row[valueIndex]));
  });

  const series = Array.from(valuesBySeries, ([name, values]): ILineChartSeries => ({
    name,
    values: labels.map((_label, position) => values.get(position) ?? null),
  }));

  return {
    labels,
    series: series.slice(0, CHART_MAX_SERIES),
    droppedSeries: Math.max(0, series.length - CHART_MAX_SERIES),
  };
};

/**
 * The lines of a line chart from the rows of its datasource, by the mapping the tool resolved.
 *
 * Cells are read by the place of their column in the datasource, never by key. A cell that is
 * not a number is a gap in its line. Lines past the cap are dropped, and counted, so the chart
 * never hands out a colour it does not have.
 */
const buildLineChartData = (
  columns: IAiChatDatasourceColumn[],
  rows: unknown[][],
  shown: ILineChartShown,
): ILineChartData => (
  shown.seriesColumn ? buildLong(columns, rows, shown, shown.seriesColumn) : buildWide(columns, rows, shown)
);

export default buildLineChartData;
